import type { Event } from '../types/Event/Event';

export type SeriesGroup = {
  type: 'series-group';
  seriesId: string;
  seriesName: string;
  events: Event[];
};

export type RecentItem = Event | SeriesGroup;

export function isSeriesGroup(item: RecentItem): item is SeriesGroup {
  return (item as SeriesGroup).type === 'series-group';
}

const BATCH_WINDOW_MS = 30 * 60 * 1000; // 30 minutes
const MIN_GROUP_SIZE = 3;

/**
 * Groups recently-added events by series when they were published in a batch.
 *
 * Input: events sorted by publishedAt DESC (as returned by the server).
 * Events with the same eventSeriesId whose publishedAt values fall within a
 * 30-minute window are grouped together. Groups of fewer than 3 events are
 * left as individual items.
 *
 * Each SeriesGroup's events are sorted by startAt ASC so the first event is
 * the next upcoming one.
 */
export function groupRecentSeriesEvents(events: Event[]): RecentItem[] {
  // Build groups keyed by seriesId, keeping events in their original order.
  // We check that consecutive (by publishedAt) events from the same series
  // are within the batch window.
  const seriesGroups = new Map<string, Event[]>();

  for (const event of events) {
    const seriesId = event.series?.id;
    if (!seriesId || !event.publishedAt) {
      continue;
    }

    const existing = seriesGroups.get(seriesId);
    if (existing) {
      // Check if this event's publishedAt is within the batch window of any
      // event already in the group (events are sorted by publishedAt DESC,
      // so compare against the first event which has the latest publishedAt)
      const latestPublishedAt = new Date(existing[0].publishedAt!).getTime();
      const thisPublishedAt = new Date(event.publishedAt).getTime();
      if (latestPublishedAt - thisPublishedAt <= BATCH_WINDOW_MS) {
        existing.push(event);
      }
      // If outside the window, don't add — it will pass through individually
    } else {
      seriesGroups.set(seriesId, [event]);
    }
  }

  // Only keep groups that meet the threshold
  const validGroups = new Map<string, Event[]>();
  for (const [seriesId, groupEvents] of seriesGroups) {
    if (groupEvents.length >= MIN_GROUP_SIZE) {
      // Sort by startAt ASC so the first event is the next upcoming
      groupEvents.sort((a, b) =>
        new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
      );
      validGroups.set(seriesId, groupEvents);
    }
  }

  // Build the result array, replacing the first occurrence of a grouped series
  // with the SeriesGroup and skipping subsequent events from that series.
  const groupedSeriesIds = new Set<string>();
  const result: RecentItem[] = [];

  for (const event of events) {
    const seriesId = event.series?.id;
    if (seriesId && validGroups.has(seriesId)) {
      if (!groupedSeriesIds.has(seriesId)) {
        // Insert the group at the position of the first event in the batch
        const groupEvents = validGroups.get(seriesId)!;
        result.push({
          type: 'series-group',
          seriesId,
          seriesName: event.series!.name || event.name,
          events: groupEvents,
        });
        groupedSeriesIds.add(seriesId);
      }
      // Skip individual events that are part of a group
    } else {
      result.push(event);
    }
  }

  return result;
}
